import type Highcharts from "highcharts";

// Shared by the Blender Asset tab (Fleet.tsx) and the blender chart pop-out,
// so both draw exactly the same chart from the same data.

export type BlenderType = "diesel" | "electric";

// GET /blenders/<name>/channels. Groups arrive in display order and only
// include groups this blender has reported; render them as returned.
export type BlenderChannel = {
  key: string;
  label: string;
  unit: string | null;
  value_type: "number" | "boolean" | "integer";
};

export type BlenderChannelGroup = {
  group: string;
  channels: BlenderChannel[];
};

export type BlenderChannelsResponse = {
  blender_name: string;
  is_electric: boolean | null;
  channel_count: number;
  groups: BlenderChannelGroup[];
};

// GET /blenders/<name>/data?format=columnar. series is keyed by blender name,
// with "t" (epoch ms, oldest first) plus one parallel array per channel key.
export type BlenderDataResponse = {
  blender_name: string;
  resolution: "1s" | "1m" | "1h";
  bucket_ms: number;
  start_time: number;
  end_time: number;
  channels: string[];
  row_count: number;
  truncated: boolean;
  series?: Record<
    string,
    { t?: number[] } & Record<string, Array<number | boolean | null>>
  >;
};

export type BlenderData = {
  blenderId: string;
  resolution: BlenderDataResponse["resolution"];
  bucketMs: number;
  startTime: number;
  endTime: number;
  channels: string[];
  rowCount: number;
  t: number[];
  // Booleans are stored as 1/0 so every channel can be charted.
  values: Record<string, Array<number | null>>;
};

// Handed from the Blender Asset tab to the pop-out through window.opener, so
// the pop-out opens instantly with the data already loaded.
export type BlenderChartHandoff = {
  key: string;
  data: BlenderData;
  channels: BlenderChannel[];
};

export const buildBlenderHandoffKey = (
  blenderId: string,
  startTime: number,
  endTime: number,
  channels: string[],
) => `blender-${blenderId}-${startTime}-${endTime}-${channels.join(",")}`;

// is_electric can be null for a unit with no one-minute data yet; fall back to
// the naming convention: electric => EFB prefix, diesel => B + number.
export const inferBlenderType = (
  blenderName: string,
  isElectric?: boolean | null,
): BlenderType => {
  if (typeof isElectric === "boolean") return isElectric ? "electric" : "diesel";
  return blenderName.toUpperCase().startsWith("EFB") ? "electric" : "diesel";
};

export const toBlenderNumber = (value: number | boolean | null | undefined) => {
  if (typeof value === "boolean") return value ? 1 : 0;
  return typeof value === "number" && Number.isFinite(value) ? value : null;
};

export const parseBlenderDataResponse = (
  body: BlenderDataResponse | undefined,
  fallback: {
    blenderId: string;
    startTime: number;
    endTime: number;
    channels: string[];
  },
): BlenderData => {
  const seriesByName = body?.series ?? {};
  const columns =
    (body?.blender_name ? seriesByName[body.blender_name] : undefined) ??
    Object.values(seriesByName)[0] ??
    {};
  const t = Array.isArray(columns.t) ? columns.t : [];
  const responseChannels = body?.channels;
  const channels =
    Array.isArray(responseChannels) && responseChannels.length > 0
      ? responseChannels
      : fallback.channels;

  const values: BlenderData["values"] = {};
  channels.forEach((key) => {
    const column = Array.isArray(columns[key]) ? columns[key] : [];
    values[key] = t.map((_, index) => toBlenderNumber(column[index]));
  });

  return {
    blenderId: body?.blender_name || fallback.blenderId,
    resolution: body?.resolution ?? "1s",
    bucketMs: body?.bucket_ms ?? 1000,
    startTime: body?.start_time ?? fallback.startTime,
    endTime: body?.end_time ?? fallback.endTime,
    channels,
    rowCount: t.length,
    t,
    values,
  };
};

// GET /blenders/<name>/live?format=columnar: the /data columnar shape plus the
// polling cursor and liveness flags.
export type BlenderLiveResponse = BlenderDataResponse & {
  server_time: number;
  max_time_stamp: number | null;
  live: boolean;
  last_advance_age_ms: number | null;
};

// Appends live rows onto the loaded data and keeps only the last `windowMs`
// of data time (measured back from the newest reading, since telemetry clocks
// run ahead of the browser). Rows are de-duplicated on time_stamp: the live
// cursor is read before the rows, so a row can come back on the next poll.
export const mergeBlenderLiveRows = (
  base: BlenderData,
  incoming: BlenderData,
  windowMs: number,
): BlenderData => {
  const channels = base.channels;
  const seen = new Set(base.t);
  const newIndexes: number[] = [];
  incoming.t.forEach((time, index) => {
    if (!seen.has(time)) {
      seen.add(time);
      newIndexes.push(index);
    }
  });
  if (newIndexes.length === 0) return base;

  const lastBaseTime = base.t.length > 0 ? base.t[base.t.length - 1] : -Infinity;
  const allAfterBase = newIndexes.every(
    (index) => incoming.t[index] > lastBaseTime,
  );

  let t: number[];
  let values: BlenderData["values"];

  if (allAfterBase) {
    // Normal case: new seconds after everything we have.
    newIndexes.sort((a, b) => incoming.t[a] - incoming.t[b]);
    t = base.t.concat(newIndexes.map((index) => incoming.t[index]));
    values = {};
    channels.forEach((key) => {
      const incomingColumn = incoming.values[key] ?? [];
      values[key] = (base.values[key] ?? []).concat(
        newIndexes.map((index) => incomingColumn[index] ?? null),
      );
    });
  } else {
    // A late row landed inside the loaded range: merge by time.
    const rows: Array<{ time: number; source: "base" | "new"; index: number }> =
      [
        ...base.t.map((time, index) => ({
          time,
          source: "base" as const,
          index,
        })),
        ...newIndexes.map((index) => ({
          time: incoming.t[index],
          source: "new" as const,
          index,
        })),
      ].sort((a, b) => a.time - b.time);
    t = rows.map((row) => row.time);
    values = {};
    channels.forEach((key) => {
      const baseColumn = base.values[key] ?? [];
      const incomingColumn = incoming.values[key] ?? [];
      values[key] = rows.map((row) =>
        row.source === "base"
          ? (baseColumn[row.index] ?? null)
          : (incomingColumn[row.index] ?? null),
      );
    });
  }

  const endTime = t[t.length - 1];
  const startTime = endTime - windowMs;
  let firstKept = 0;
  while (firstKept < t.length && t[firstKept] < startTime) firstKept += 1;
  if (firstKept > 0) {
    t = t.slice(firstKept);
    channels.forEach((key) => {
      values[key] = values[key].slice(firstKept);
    });
  }

  return {
    ...base,
    // Live rows are always raw 1s data.
    resolution: "1s",
    bucketMs: 1000,
    startTime,
    endTime,
    rowCount: t.length,
    t,
    values,
  };
};

// Turns a /data error response into a message for the user.
export const describeBlenderDataError = (data: any): string => {
  if (data?.code === "RANGE_TOO_LARGE" && data?.max_range_ms) {
    const maxDays = Math.floor(data.max_range_ms / 86_400_000);
    return `Time range is too large. The maximum is ${maxDays} days.`;
  }
  if (data?.code === "CHANNEL_INVALID") {
    return `Unknown channel(s): ${(data.unknown_channels ?? []).join(", ")}`;
  }
  if (typeof data?.message === "string" && data.message) return data.message;
  if (typeof data?.error === "string" && data.error) return data.error;
  return "Failed to load blender data.";
};

export const formatBlenderTimestamp = (epochMs: number) =>
  new Date(epochMs).toLocaleString("en-US", {
    month: "2-digit",
    day: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });

// Flags are exact on/off at 1s, but the fraction of the bucket they were on
// at 1m/1h; codes are exact at 1s and a bucket average otherwise.
export const formatBlenderValue = (
  value: number | null,
  channel: BlenderChannel | undefined,
  resolution: BlenderData["resolution"],
) => {
  if (value === null) return "—";
  if (channel?.value_type === "boolean") {
    return resolution === "1s"
      ? value
        ? "On"
        : "Off"
      : `${Math.round(value * 100)}% on`;
  }
  if (channel?.value_type === "integer" && resolution === "1s") {
    return String(value);
  }
  // Show the stored precision (trailing zeros dropped) so small decimal
  // changes are visible, without float noise like 45.650000001.
  return value.toLocaleString("en-US", { maximumFractionDigits: 4 });
};

const blenderAxisTitle = (
  channel: BlenderChannel | undefined,
  resolution: BlenderData["resolution"],
) => {
  if (channel?.unit) return channel.unit;
  if (channel?.value_type === "boolean") {
    return resolution === "1s" ? "On / Off" : "Fraction on";
  }
  if (channel?.value_type === "integer") return "Code";
  return "Value";
};

// A zoomed view: the x window plus, when the user dragged a box, a y range
// per y-axis (indexed like the chart's yAxis array).
export type BlenderChartView = {
  xMin: number;
  xMax: number;
  yRanges?: Array<{ min: number; max: number } | null>;
};

// Points drawn per series for the visible window: about one min/max pair per
// horizontal pixel, so the line looks the same as drawing every sample.
export const BLENDER_CHART_MAX_POINTS = 4000;

type ChartPoint = [number, number | null];

// Full-resolution points for one channel, with a null inserted at each gap so
// the line breaks instead of bridging missing data.
const buildChannelPoints = (
  t: number[],
  channelValues: Array<number | null>,
  isDiscrete: boolean,
  gapMs: number,
): ChartPoint[] => {
  const data: ChartPoint[] = [];
  let previousTime: number | null = null;
  let previousValue: number | null = null;
  // Step series only need their transitions, which keeps multi-day 1s
  // flag/code series small. A skipped point is flushed before a gap or
  // at the end so the step still extends to its last reading.
  let skippedPoint: [number, number] | null = null;

  for (let index = 0; index < t.length; index += 1) {
    const value = channelValues[index];
    if (value === null || value === undefined) continue;

    const time = t[index];
    const isAfterGap = previousTime !== null && time - previousTime > gapMs;
    if (isAfterGap) {
      if (skippedPoint) data.push(skippedPoint);
      skippedPoint = null;
      data.push([previousTime! + 1, null]);
    }

    if (isDiscrete && !isAfterGap && previousValue === value) {
      skippedPoint = [time, value];
    } else {
      data.push([time, value]);
      skippedPoint = null;
    }
    previousTime = time;
    previousValue = value;
  }
  if (skippedPoint) data.push(skippedPoint);
  return data;
};

// First index whose x is >= target (points are sorted by x).
const lowerBound = (points: ChartPoint[], target: number) => {
  let low = 0;
  let high = points.length;
  while (low < high) {
    const mid = (low + high) >> 1;
    if (points[mid][0] < target) low = mid + 1;
    else high = mid;
  }
  return low;
};

// Min/max-per-bucket downsample of one gap-free run. Keeps every local
// extreme, so spikes and small decimal steps survive at any zoom level.
const minMaxRun = (
  run: Array<[number, number]>,
  maxPoints: number,
): Array<[number, number]> => {
  const n = run.length;
  if (n <= maxPoints) return run;

  const buckets = Math.max(1, Math.floor(maxPoints / 2));
  const bucketSize = n / buckets;
  const out: Array<[number, number]> = [run[0]];

  for (let bucket = 0; bucket < buckets; bucket += 1) {
    const start = Math.floor(bucket * bucketSize);
    const end = Math.min(n, Math.floor((bucket + 1) * bucketSize));
    if (start >= end) continue;

    let minIndex = start;
    let maxIndex = start;
    for (let index = start + 1; index < end; index += 1) {
      if (run[index][1] < run[minIndex][1]) minIndex = index;
      if (run[index][1] > run[maxIndex][1]) maxIndex = index;
    }
    if (minIndex === maxIndex) out.push(run[minIndex]);
    else if (minIndex < maxIndex) out.push(run[minIndex], run[maxIndex]);
    else out.push(run[maxIndex], run[minIndex]);
  }

  out.push(run[n - 1]);
  return out;
};

// Slice to the visible window (plus one point either side so the line reaches
// the edges), then downsample each gap-free run within the point budget.
const pointsForWindow = (
  points: ChartPoint[],
  xMin: number,
  xMax: number,
  maxPoints: number,
): ChartPoint[] => {
  const start = Math.max(0, lowerBound(points, xMin) - 1);
  const end = Math.min(points.length, lowerBound(points, xMax) + 1);
  const visible = points.slice(start, end);

  const runs: Array<Array<[number, number]>> = [];
  const breaks: ChartPoint[] = [];
  let current: Array<[number, number]> = [];
  visible.forEach((point) => {
    if (point[1] === null) {
      runs.push(current);
      breaks.push(point);
      current = [];
    } else {
      current.push(point as [number, number]);
    }
  });
  runs.push(current);

  const total = runs.reduce((sum, run) => sum + run.length, 0);
  if (total <= maxPoints) return visible;

  const out: ChartPoint[] = [];
  runs.forEach((run, index) => {
    const budget = Math.max(2, Math.round((maxPoints * run.length) / total));
    out.push(...minMaxRun(run, budget));
    if (index < breaks.length) out.push(breaks[index]);
  });
  return out;
};

export const buildBlenderChartOptions = (
  blenderData: BlenderData | null,
  channelByKey: Map<string, BlenderChannel>,
  options: {
    height?: number | string | null;
    showTitle?: boolean;
    view?: BlenderChartView | null;
    maxPoints?: number;
    // Called with the box the user dragged. The chart itself does not zoom;
    // the caller stores the view and rebuilds the options for it.
    onSelectView?: (view: BlenderChartView) => void;
  } = {},
): Highcharts.Options | null => {
  if (!blenderData || blenderData.rowCount === 0) return null;

  const { t, values, resolution, bucketMs } = blenderData;
  const view = options.view ?? null;
  const xMin = view?.xMin ?? blenderData.startTime;
  const xMax = view?.xMax ?? blenderData.endTime;
  const maxPoints = options.maxPoints ?? BLENDER_CHART_MAX_POINTS;
  // Rows only exist where some selected channel had a value, so a jump in
  // time is a real gap. Break the line there instead of bridging it.
  const gapMs = Math.max(bucketMs * 5, 5000);

  // One y-axis per unit (or per flag/code kind) so channels with very
  // different scales stay readable.
  const axisIndexByTitle = new Map<string, number>();
  const yAxis: Highcharts.YAxisOptions[] = [];

  const series = blenderData.channels.map((channelKey) => {
    const channel = channelByKey.get(channelKey);
    const isDiscrete = !!channel && channel.value_type !== "number";
    const isBoolean = channel?.value_type === "boolean";
    const axisTitle = blenderAxisTitle(channel, resolution);

    let axisIndex = axisIndexByTitle.get(axisTitle);
    if (axisIndex === undefined) {
      axisIndex = yAxis.length;
      axisIndexByTitle.set(axisTitle, axisIndex);
      const yRange = view?.yRanges?.[axisIndex] ?? null;
      yAxis.push({
        title: { text: axisTitle, style: { color: "#9ca3af" } },
        labels: { style: { color: "#9ca3af" } },
        gridLineColor: "rgba(156, 163, 175, 0.14)",
        opposite: axisIndex % 2 === 1,
        // Fit the visible data tightly instead of rounding out to the next
        // tick, so small changes use the full chart height.
        startOnTick: false,
        endOnTick: false,
        minPadding: 0.04,
        maxPadding: 0.04,
        // min/max are always set (null = auto) so an in-place chart update
        // after Reset zoom clears the previous zoomed range.
        ...(yRange
          ? { min: yRange.min, max: yRange.max }
          : isBoolean
            ? { min: 0, max: 1, tickInterval: resolution === "1s" ? 1 : 0.25 }
            : { min: null, max: null }),
      });
    }

    const points = buildChannelPoints(
      t,
      values[channelKey] ?? [],
      isDiscrete,
      gapMs,
    );

    return {
      type: "line" as const,
      name: channel?.unit
        ? `${channel.label} (${channel.unit})`
        : (channel?.label ?? channelKey),
      data: pointsForWindow(points, xMin, xMax, maxPoints),
      yAxis: axisIndex,
      custom: { channelKey },
      connectNulls: false,
      // On/off flags and codes are discrete, so draw them as steps.
      step: isDiscrete ? ("left" as const) : undefined,
      animation: false,
      // Markers turn on by themselves once zoomed in far enough that the
      // individual 1s samples are spread apart.
      marker: {
        enabledThreshold: 4,
        radius: 2.5,
        states: { hover: { enabled: true, radius: 4 } },
      },
    };
  });

  const onSelectView = options.onSelectView;

  return {
    chart: {
      backgroundColor: "transparent",
      height: options.height === undefined ? 390 : options.height,
      animation: false,
      // Drag a box to zoom both time and value, e.g. around one plateau to
      // see its decimal changes.
      zooming: { type: "xy" },
      events: onSelectView
        ? {
            selection(event) {
              const xSelection = event.xAxis?.[0];
              if (!xSelection) return false;
              onSelectView({
                xMin: xSelection.min,
                xMax: xSelection.max,
                yRanges: yAxis.map((_, index) => {
                  const ySelection = event.yAxis?.find(
                    (item) => (item.axis as any)?.index === index,
                  );
                  return ySelection
                    ? { min: ySelection.min, max: ySelection.max }
                    : null;
                }),
              });
              // The caller rebuilds the chart for the new view.
              return false;
            },
          }
        : undefined,
    },
    title:
      options.showTitle === false
        ? { text: undefined }
        : {
            text: `${blenderData.blenderId} • Blender Data (${resolution})`,
            style: { color: "var(--color-text)", fontSize: "15px" },
          },
    xAxis: {
      type: "datetime",
      min: xMin,
      max: xMax,
      labels: { style: { color: "#9ca3af" } },
      lineColor: "#59636e",
      tickColor: "#59636e",
    },
    yAxis,
    legend: {
      itemStyle: { color: "#d1d5db", fontWeight: "500" },
      itemHoverStyle: { color: "#ffffff" },
    },
    credits: { enabled: false },
    exporting: { enabled: false },
    tooltip: {
      shared: true,
      xDateFormat: "%m/%d/%Y %I:%M:%S %p",
      pointFormatter: function (this: Highcharts.Point) {
        const channelKey = (this.series.options as any).custom?.channelKey;
        const value = formatBlenderValue(
          typeof this.y === "number" ? this.y : null,
          channelByKey.get(channelKey),
          resolution,
        );
        return `<span style="color:${this.color}">●</span> ${this.series.name}: <b>${value}</b><br/>`;
      },
    },
    plotOptions: {
      series: { animation: false, turboThreshold: 0 },
    },
    // Each series is already reduced to the visible window, so plain SVG is
    // fast enough. Boost is off because it broke tooltip hit-testing.
    boost: { enabled: false },
    series,
  };
};
